import React, { useState } from 'react';
import { Shield, Stethoscope, Users, Bell, Search, MapPin } from 'lucide-react';
import { useApplicationSettings } from '../../../contexts/ApplicationSettingsContext';
import type { Insurance, Condition, Provider, Location } from '../types/moderator.types';

export interface OverviewSectionProps {
  insurances: Insurance[];
  conditions: Condition[];
  providers: Provider[];
  locations: Location[];
}

export const OverviewSection: React.FC<OverviewSectionProps> = ({
  insurances,
  conditions,
  providers,
  locations,
}) => {
  const { settings } = useApplicationSettings();
  const [globalSearch, setGlobalSearch] = useState('');

  return (
    <div className="space-y-6">
      {/* Global Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search across all data (insurances, conditions, locations, providers)..."
          value={globalSearch}
          onChange={(e) => setGlobalSearch(e.target.value)}
          className="w-full px-4 py-2 pl-10 border rounded-lg transition-colors"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            color: 'var(--color-text-primary)',
            '--tw-ring-color': 'var(--color-accent)'
          } as React.CSSProperties}
        />
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Insurances Card */}
        <div 
          className="rounded-lg border p-6"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {insurances.length}
              </p>
              <p style={{ color: 'var(--color-text-secondary)' }}>Insurance Plans</p>
            </div>
            <Shield className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
          </div>
        </div>
        
        {/* Conditions Card */}
        <div 
          className="rounded-lg border p-6"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {conditions.length}
              </p>
              <p style={{ color: 'var(--color-text-secondary)' }}>Conditions</p>
            </div>
            <Stethoscope className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
          </div>
        </div>
        
        {/* Providers Card */}
        <div 
          className="rounded-lg border p-6"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {providers.length}
              </p>
              <p style={{ color: 'var(--color-text-secondary)' }}>Providers</p>
            </div>
            <Users className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
          </div>
        </div>

        {/* Locations Card */}
        <div 
          className="rounded-lg border p-6"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)'
          }}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {locations.length}
              </p>
              <p style={{ color: 'var(--color-text-secondary)' }}>Locations</p>
            </div>
            <MapPin className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
          </div>
        </div>

        {/* Bulletins Card (conditional) */}
        {settings?.bulletinsEnabled && (
          <div 
            className="rounded-lg border p-6"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)'
            }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>-</p>
                <p style={{ color: 'var(--color-text-secondary)' }}>Active Bulletins</p>
              </div>
              <Bell className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

